import React, { useEffect, useState, useRef } from 'react';
import { ClockIcon, ChevronDownIcon } from 'lucide-react';
export type Granularity = 'hourly' | 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
interface DateRange {
  start: Date;
  end: Date;
}
/** Compute which granularities are available based on the selected date range */
export function getAvailableGranularities(dateRange: DateRange): Granularity[] {
  const diffMs = dateRange.end.getTime() - dateRange.start.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  if (diffDays <= 1) {
    // Today or yesterday — hourly by default, no selector shown
    return ['hourly'];
  } else if (diffDays <= 7) {
    // >1 day to 1 week
    return ['hourly', 'daily'];
  } else if (diffDays <= 31) {
    // >1 week to 1 month
    return ['daily', 'weekly'];
  } else if (diffDays <= 365) {
    // >1 month to 1 year
    return ['daily', 'weekly', 'monthly'];
  } else {
    // >1 year
    return ['monthly', 'yearly'];
  }
}
/** Returns the best default granularity for a given date range */
export function getDefaultGranularity(dateRange: DateRange): Granularity {
  const available = getAvailableGranularities(dateRange);
  return available[0];
}
interface GranularitySelectorProps {
  value: Granularity;
  onChange: (granularity: Granularity) => void;
  allowedGranularities?: Granularity[];
  hidden?: boolean;
}
export function GranularitySelector({
  value,
  onChange,
  allowedGranularities,
  hidden = false
}: GranularitySelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const allOptions: {
    value: Granularity;
    label: string;
  }[] = [{
    value: 'hourly',
    label: 'Hourly'
  }, {
    value: 'daily',
    label: 'Daily'
  }, {
    value: 'weekly',
    label: 'Weekly'
  }, {
    value: 'monthly',
    label: 'Monthly'
  }, {
    value: 'quarterly',
    label: 'Quarterly'
  }, {
    value: 'yearly',
    label: 'Yearly'
  }];
  const options = allowedGranularities ? allOptions.filter(opt => allowedGranularities.includes(opt.value)) : allOptions;
  const selectedOption = options.find(opt => opt.value === value) || options[0];
  if (hidden) return null;
  return <div className="relative" ref={dropdownRef} data-id="element-1914" data-mp="1dvnve88he9zk">
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1915" data-mp="1rpxbb62l9ogx">
        
        <ClockIcon size={16} data-id="element-1916" data-mp="z7jcp49fhygs" />
        <span data-id="element-1917" data-mp="z6rtyu0xjv7x">{selectedOption?.label}</span>
        <ChevronDownIcon size={16} data-id="element-1918" data-mp="ua1bk01nknjc" />
      </button>

      {isOpen && <div className="absolute left-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50" data-id="element-1919" data-mp="u7f4s2ml6c26">
          {options.map(option => <button key={option.value} onClick={() => {
        onChange(option.value);
        setIsOpen(false);
      }} className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${value === option.value ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700'}`} data-id="element-1920" data-mp="3ao3fkptyn6l9">
          
              {option.label}
            </button>)}
        </div>}
    </div>;
}